import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { OperatorCustomNotificationAction } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.model';
import { OPERATOR_CUSTOM_NOTIFICATIONS_BULK_ACTIONS_I18N_PREFIX } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.constants';
import { toAmountKeySegment } from 'modules/settings/pods/settings-operator-custom-notifications/operator-custom-notification-amount.business';
import {
    deleteOperatorCustomNotifications,
    updateOperatorCustomNotificationsActiveStatus,
} from 'modules/settings/pods/settings-operator-custom-notifications/service/operator-custom-notifications.service';

const feedbackKey = (action: OperatorCustomNotificationAction, outcome: 'success' | 'error', ids: string[]) =>
    `${OPERATOR_CUSTOM_NOTIFICATIONS_BULK_ACTIONS_I18N_PREFIX}.${action}.notifications.${outcome}.${toAmountKeySegment(
        ids.length
    )}`;

export const useOperatorCustomNotificationsWrite = () => {
    const { t } = useTranslation();

    const resyncList = () => void updateQuery(settingsKeys.operatorCustomNotifications());

    const notifyOutcome = (action: OperatorCustomNotificationAction) => ({
        onSuccess: (_data: void, ids: string[]) => showSuccessNotification(t(feedbackKey(action, 'success', ids))),
        onError: (_error: unknown, ids: string[]) => showErrorNotification(t(feedbackKey(action, 'error', ids))),
        onSettled: resyncList,
    });

    const { mutateAsync: activate } = usePocMutation({
        mutationFn: (ids: string[]) => updateOperatorCustomNotificationsActiveStatus(ids, true),
        ...notifyOutcome('activate'),
    });

    const { mutateAsync: deactivate } = usePocMutation({
        mutationFn: (ids: string[]) => updateOperatorCustomNotificationsActiveStatus(ids, false),
        ...notifyOutcome('deactivate'),
    });

    const { mutateAsync: remove } = usePocMutation({
        mutationFn: (ids: string[]) => deleteOperatorCustomNotifications(ids),
        ...notifyOutcome('delete'),
    });

    return { activate, deactivate, remove };
};
